import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr'
import { signalrConfig } from '../../configs/signalr'
import authProvider from '../AuthenticationProvider'
import EventBus from './EventBus'
import { formatMessageData } from './helper'

class SignalrMessageHub extends EventBus {
  messageChannelToHubs = {} // map hubs to a label
  remoteChannelToHubs = {} // rpc
  queuedRemoteCalls = [] // if a rpc posted while connection down, push it to a queue
  runQueueThread = null

  constructor() {
    super()
    this.init()
  }

  // iterate all signalr config and create connections
  init() {
    signalrConfig.Hubs.forEach((hub) => {
      const connection = new HubConnectionBuilder()
        .withUrl(hub.ServerUrl, {
          transport: HttpTransportType.WebSockets, //HttpTransportType.LongPolling, // | HttpTransportType.WebSockets // websocket will throw error, can not resolve this yet
          accessTokenFactory: () =>
            authProvider.getUser().then((user) => user.access_token),
          skipNegotiation: true,
        })
        .configureLogging(LogLevel.Error)
        .build()

      //map hub connection to a label
      this.messageChannelToHubs[hub.Prefix] = connection

      connection.on('ReceivedMessage', (message) => {
        this.publish(
          message.channel,
          formatMessageData(message.channel, message.data),
        )
      })

      //do reconnect
      connection.onclose((e) =>
        setTimeout(() => this.connect(connection), 5000),
      )

      this.connect(connection)

      //register rpc, map a rpc to a hub
      hub.RegisteredRemoteChannels.forEach((channel, index) => {
        if (this.remoteChannelToHubs[channel]) {
          throw new Error('Remote channel is duplicated: ' + channel)
        }
        this.remoteChannelToHubs[channel] = connection
      })
    })
  }

  async connect(hub) {
    let self = this
    await hub
      .start()
      .then(() => self.runQueue())
      .catch((err) => {
        console.log(err)

        //try re-connecting after 5s
        setTimeout(() => {
          console.log('reconnect')
          this.connect(hub)
        }, 5000)
      })
  }

  //push an action to the queue
  //it is posible for a race condition
  ensure(f) {
    this.queuedRemoteCalls.push(f)
  }

  runQueue() {
    var fails = []
    while (this.queuedRemoteCalls && this.queuedRemoteCalls.length > 0) {
      var f = this.queuedRemoteCalls.shift()
      if (!f()) {
        fails.push(f)
      }
    }
    for (let f of fails) {
      this.queuedRemoteCalls.push(f)
    }
    if (this.runQueueThread != null) {
      clearTimeout(this.runQueueThread)
    }
    this.runQueueThread = setTimeout(() => {
      this.runQueue()
    }, 1000)
  }

  invoke(channel, message, fail) {
    this.remoteChannelToHubs[channel]
      .invoke(channel, message)
      .catch((error) => fail(error))
  }

  async invokeUserConnected(userId) {
    const hubPrefix = Object.keys(this.messageChannelToHubs)[0]
    const connection = this.messageChannelToHubs[hubPrefix]
    if (connection && connection.state === HubConnectionState.Connected) {
      try {
        await connection.invoke('UserConnected')
      } catch (err) {
        console.error('Invoke UserConnected failed:', err)
      }
    } else {
      // Nếu chưa kết nối, đợi kết nối xong rồi invoke
      this.ensure(() => {
        if (connection && connection.state === HubConnectionState.Connected) {
          connection.invoke('UserConnected')
          return true
        }
        return false
      })
    }
  }

  subscribe(channel, action) {
    const [group, hubPrefix, key] = channel.split('.')
    const id = [hubPrefix, key].join('.')

    if (!this.channels[id]) {
      this.channels[id] = []

      if (this.messageChannelToHubs[hubPrefix] && !this.groups[group]) {
        this.groups[group] = true

        if (
          this.messageChannelToHubs[hubPrefix].connection.connectionState === 1
        ) {
          this.messageChannelToHubs[hubPrefix].invoke('JoinGroup', group)
        } else {
          let self = this
          this.ensure(() => {
            if (
              self.messageChannelToHubs[hubPrefix] &&
              self.messageChannelToHubs[hubPrefix].connection
                .connectionState === 1
            ) {
              self.messageChannelToHubs[hubPrefix].invoke('JoinGroup', group)
              return true
            }
            return false
          })
        }
      }
    }

    this.channels[id].push(action)
  }

  unsubscribe(channel, action) {
    const [group, hubPrefix, key] = channel.split('.')
    const id = [hubPrefix, key].join('.')

    if (this.channels[id]) {
      this.channels[id] = this.channels[id].filter((a) => a !== action)

      if (this.channels[id].length === 0) {
        delete this.channels[id]
      }

      if (
        !this.channels[id] &&
        this.messageChannelToHubs[hubPrefix] &&
        this.groups[group]
      ) {
        this.groups[group] = false
        if (
          this.messageChannelToHubs[hubPrefix].connection.connectionState === 1
        ) {
          this.messageChannelToHubs[hubPrefix].invoke('LeaveGroup', group)
        } else {
          let self = this
          this.ensure(() => {
            if (
              this.messageChannelToHubs[hubPrefix] &&
              this.messageChannelToHubs[hubPrefix].connection
                .connectionState === 1
            ) {
              self.messageChannelToHubs[hubPrefix].invoke('LeaveGroup', group)
              return true
            }

            return false
          })
        }
      }
    }
  }
}

const MessageHub = new SignalrMessageHub()
export default MessageHub
